// zh-Hans values are hand-translated; zh-Hant values are mechanically
// derived from zh-Hans via OpenCC (`opencc-js`, `from: 'cn', to: 'twp'` —
// the phrase-level Taiwan-vocabulary variant, not just character
// substitution, e.g. it correctly renders UAE as 阿拉伯聯合大公國 rather
// than a literal character-swap of the mainland term) — don't hand-edit
// zh-Hant, regenerate it if zh-Hans changes. One entry (Mali) was
// hand-corrected after conversion: OpenCC's default 里→裡 substitution is
// context-dependent and got this proper noun wrong (馬裡 "inside" vs the
// correct 馬里 country name).
import type { Continent } from '@/types'

export const CONTINENT_NAMES_ZH_HANS: Record<Continent, string> = {
  "Africa": "非洲",
  "Antarctica": "南极洲",
  "Asia": "亚洲",
  "Europe": "欧洲",
  "North America": "北美洲",
  "Oceania": "大洋洲",
  "South America": "南美洲",
}

export const CONTINENT_NAMES_ZH_HANT: Record<Continent, string> = {
  "Africa": "非洲",
  "Antarctica": "南極洲",
  "Asia": "亞洲",
  "Europe": "歐洲",
  "North America": "北美洲",
  "Oceania": "大洋洲",
  "South America": "南美洲",
}

/** Keyed by the exact canonical `WorldObject.country` string. */
export const COUNTRY_NAMES_ZH_HANS: Record<string, string> = {
  "Antarctica": "南极洲",
  "Argentina": "阿根廷",
  "Argentina & Brazil": "阿根廷和巴西",
  "Australia": "澳大利亚",
  "Austria": "奥地利",
  "Bolivia": "玻利维亚",
  "Botswana": "博茨瓦纳",
  "Brazil": "巴西",
  "Cambodia": "柬埔寨",
  "Canada": "加拿大",
  "Canada & United States": "加拿大和美国",
  "Chile": "智利",
  "China": "中国",
  "Colombia": "哥伦比亚",
  "Croatia": "克罗地亚",
  "Cuba": "古巴",
  "Czech Republic": "捷克共和国",
  "Democratic Republic of the Congo": "刚果民主共和国",
  "Ecuador": "厄瓜多尔",
  "Egypt": "埃及",
  "Ethiopia": "埃塞俄比亚",
  "Fiji": "斐济",
  "Finland": "芬兰",
  "France": "法国",
  "French Polynesia": "法属波利尼西亚",
  "Germany": "德国",
  "Greece": "希腊",
  "Hong Kong": "香港",
  "Hungary": "匈牙利",
  "Iceland": "冰岛",
  "India": "印度",
  "Indonesia": "印度尼西亚",
  "Ireland": "爱尔兰",
  "Italy": "意大利",
  "Japan": "日本",
  "Jordan": "约旦",
  "Jordan & Israel": "约旦和以色列",
  "Kenya": "肯尼亚",
  "Madagascar": "马达加斯加",
  "Malaysia": "马来西亚",
  "Maldives": "马尔代夫",
  "Mali": "马里",
  "Mexico": "墨西哥",
  "Mongolia": "蒙古",
  "Morocco": "摩洛哥",
  "Myanmar": "缅甸",
  "Namibia": "纳米比亚",
  "Nepal": "尼泊尔",
  "Netherlands": "荷兰",
  "New Zealand": "新西兰",
  "Norway": "挪威",
  "Pakistan": "巴基斯坦",
  "Palau": "帕劳",
  "Panama": "巴拿马",
  "Peru": "秘鲁",
  "Philippines": "菲律宾",
  "Portugal": "葡萄牙",
  "Qatar": "卡塔尔",
  "Romania": "罗马尼亚",
  "Russia": "俄罗斯",
  "Rwanda": "卢旺达",
  "Singapore": "新加坡",
  "South Africa": "南非",
  "South Korea": "韩国",
  "Spain": "西班牙",
  "Sri Lanka": "斯里兰卡",
  "Switzerland": "瑞士",
  "Tanzania": "坦桑尼亚",
  "Thailand": "泰国",
  "Turkey": "土耳其",
  "United Arab Emirates": "阿拉伯联合酋长国",
  "United Kingdom": "英国",
  "United States": "美国",
  "United States & Canada": "美国和加拿大",
  "Uzbekistan": "乌兹别克斯坦",
  "Vanuatu": "瓦努阿图",
  "Vatican City": "梵蒂冈",
  "Venezuela": "委内瑞拉",
  "Vietnam": "越南",
  "Yemen": "也门",
  "Zambia & Zimbabwe": "赞比亚和津巴布韦",
  "Zimbabwe": "津巴布韦",
}

export const COUNTRY_NAMES_ZH_HANT: Record<string, string> = {
  "Antarctica": "南極洲",
  "Argentina": "阿根廷",
  "Argentina & Brazil": "阿根廷和巴西",
  "Australia": "澳大利亞",
  "Austria": "奧地利",
  "Bolivia": "玻利維亞",
  "Botswana": "波札那",
  "Brazil": "巴西",
  "Cambodia": "柬埔寨",
  "Canada": "加拿大",
  "Canada & United States": "加拿大和美國",
  "Chile": "智利",
  "China": "中國",
  "Colombia": "哥倫比亞",
  "Croatia": "克羅埃西亞",
  "Cuba": "古巴",
  "Czech Republic": "捷克共和國",
  "Democratic Republic of the Congo": "剛果民主共和國",
  "Ecuador": "厄瓜多",
  "Egypt": "埃及",
  "Ethiopia": "衣索比亞",
  "Fiji": "斐濟",
  "Finland": "芬蘭",
  "France": "法國",
  "French Polynesia": "法屬波利尼西亞",
  "Germany": "德國",
  "Greece": "希臘",
  "Hong Kong": "香港",
  "Hungary": "匈牙利",
  "Iceland": "冰島",
  "India": "印度",
  "Indonesia": "印度尼西亞",
  "Ireland": "愛爾蘭",
  "Italy": "義大利",
  "Japan": "日本",
  "Jordan": "約旦",
  "Jordan & Israel": "約旦和以色列",
  "Kenya": "肯亞",
  "Madagascar": "馬達加斯加",
  "Malaysia": "馬來西亞",
  "Maldives": "馬爾地夫",
  "Mali": "馬里",
  "Mexico": "墨西哥",
  "Mongolia": "蒙古",
  "Morocco": "摩洛哥",
  "Myanmar": "緬甸",
  "Namibia": "納米比亞",
  "Nepal": "尼泊爾",
  "Netherlands": "荷蘭",
  "New Zealand": "紐西蘭",
  "Norway": "挪威",
  "Pakistan": "巴基斯坦",
  "Palau": "帛琉",
  "Panama": "巴拿馬",
  "Peru": "秘魯",
  "Philippines": "菲律賓",
  "Portugal": "葡萄牙",
  "Qatar": "卡達",
  "Romania": "羅馬尼亞",
  "Russia": "俄羅斯",
  "Rwanda": "盧安達",
  "Singapore": "新加坡",
  "South Africa": "南非",
  "South Korea": "韓國",
  "Spain": "西班牙",
  "Sri Lanka": "斯里蘭卡",
  "Switzerland": "瑞士",
  "Tanzania": "坦尚尼亞",
  "Thailand": "泰國",
  "Turkey": "土耳其",
  "United Arab Emirates": "阿拉伯聯合大公國",
  "United Kingdom": "英國",
  "United States": "美國",
  "United States & Canada": "美國和加拿大",
  "Uzbekistan": "烏茲別克",
  "Vanuatu": "萬那杜",
  "Vatican City": "梵蒂岡",
  "Venezuela": "委內瑞拉",
  "Vietnam": "越南",
  "Yemen": "葉門",
  "Zambia & Zimbabwe": "尚比亞和辛巴威",
  "Zimbabwe": "辛巴威",
}

